import React, { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import BrandLoader from '@core/components/ui/BrandLoader';
import { useAuth } from '@core/contexts/AuthContext';

interface AuthGuardProps {
  children: ReactNode;
  redirectTo?: string;
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  redirectTo = '/login',
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Listen for logout events
  useEffect(() => {
    const handleLogout = () => {
      setIsLoggingOut(true);
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 400);
    };
    window.addEventListener('logout', handleLogout);
    return () => {
      window.removeEventListener('logout', handleLogout);
    };
  }, []);

  if (loading && !user) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="background.default"
      >
        <BrandLoader size={120} />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return (
    <Box
      sx={{
        filter: isLoggingOut ? 'blur(8px)' : 'none',
        transition: 'filter 0.4s ease-in-out',
        position: 'relative',
        '&::before': isLoggingOut
          ? {
              content: '""',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              zIndex: 9999,
              pointerEvents: 'none',
            }
          : {},
      }}
    >
      {children}
    </Box>
  );
};

export default AuthGuard;
